import type { LeaderboardReplay } from './replayTypes'
import { RundotApiError } from '../errors'
import { utf8ByteLength } from '../utils/utf8'

/**
 * Inline replay-size cap for `requiresReplay` leaderboard submissions.
 *
 * Set from the M8.5 Task-0 measurement: a representative solo run's
 * `exportReplay()` serializes to ~0.28 KB/frame of JSON, so a 2-minute 60 Hz
 * run is ~2 MB. 4 MB gives ~2× headroom over that while still bounding an
 * authenticated payload. This is the SDK's UX-facing limit — the functions
 * submit boundary and the cloud-run verifier enforce their own copies (a
 * direct authenticated call bypasses this one).
 */
export const REPLAY_INLINE_MAX_BYTES = 4 * 1024 * 1024

/**
 * Reject a replay that serializes above the inline cap BEFORE any submission is
 * attempted, so the game gets an actionable error instead of a silent transport
 * failure or a server 413.
 */
export function assertReplayWithinInlineCap(replay: LeaderboardReplay): void {
  const bytes = utf8ByteLength(JSON.stringify(replay))
  if (bytes > REPLAY_INLINE_MAX_BYTES) {
    throw new RundotApiError(
      'PAYLOAD_TOO_LARGE',
      `Replay of ${bytes} bytes exceeds the inline submission cap of ${REPLAY_INLINE_MAX_BYTES} bytes. ` +
        'Shorten the run or contact support about oversize replay uploads.',
      413,
    )
  }
}
